/**
 * React hook wrapping `pushService`.
 *
 * Usage — mount once on the authenticated layout:
 *
 *   const push = usePushNotifications({ onMessage: (m) => toast.show(m) });
 *   // push.support, push.permission, push.enable(), push.disable()
 *
 * Behaviour:
 *   - On mount (while authenticated) refreshes the FCM token silently if
 *     permission is already granted. No UI side effects.
 *   - Reacts to logout by calling `pushService.disable()`.
 *   - Re-renders the consumer when permission / support state changes.
 *   - Optionally surfaces foreground + background-click messages via the
 *     `onMessage` callback.
 *
 * IMPORTANT — `enable()` MUST be invoked from a user-gesture handler (a
 * click). Browsers block `Notification.requestPermission()` otherwise.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { updateDeviceInfo } from '@/features/auth/api';
import { pushService } from './pushService';
import type { EnableResult } from './pushService';
import {
  PUSH_MESSAGE_EVENT,
  type IncomingPushMessage,
  type PushPermission,
  type PushSupport,
} from './types';

export interface UsePushNotificationsOptions {
  /** Fired for foreground messages AND background-notification clicks. */
  onMessage?: (msg: IncomingPushMessage) => void;
  /**
   * Auto-refresh the FCM token on mount when permission is already granted.
   * Defaults to `true`. Set to `false` for screens where you only want
   * manual `enable()` calls (e.g. a settings page toggle).
   */
  autoRefresh?: boolean;
}

export interface UsePushNotificationsReturn {
  support: PushSupport;
  permission: PushPermission;
  /** True if the user previously dismissed our prompt. */
  optedOut: boolean;
  /** Last token registered with the backend (or null). */
  token: string | null;
  /** Trigger permission prompt → token → backend register. */
  enable: () => Promise<EnableResult>;
  isEnabling: boolean;
  enableError: string | null;
  /** Logout / explicit opt-out. */
  disable: () => Promise<void>;
  /** Mark "not now" so the prompt stops appearing. */
  dismiss: () => void;
}

export function usePushNotifications(
  options: UsePushNotificationsOptions = {},
): UsePushNotificationsReturn {
  const { onMessage, autoRefresh = true } = options;

  const user = useAuthStore((s) => s.user);
  const authToken = useAuthStore((s) => s.token);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [support, setSupport] = useState<PushSupport>(() => pushService.detectSupport());
  const [permission, setPermission] = useState<PushPermission>(() => pushService.getPermission());
  const [optedOut, setOptedOut] = useState<boolean>(() => pushService.hasOptedOut());
  const [token, setToken] = useState<string | null>(() => pushService.getRegisteredToken());
  const [isEnabling, setIsEnabling] = useState(false);
  const [enableError, setEnableError] = useState<string | null>(null);

  // ---- state sync (driven by pushService) ----------------------------------
  useEffect(() => {
    const sync = () => {
      setSupport(pushService.detectSupport());
      setPermission(pushService.getPermission());
      setOptedOut(pushService.hasOptedOut());
      setToken(pushService.getRegisteredToken());
    };
    return pushService.subscribe(sync);
  }, []);

  // ---- auto-refresh on cold start ------------------------------------------
  useEffect(() => {
    if (!autoRefresh) return;
    if (!isAuthenticated || !user?.id) return;
    if (support !== 'supported') return;
    if (permission !== 'granted') return;
    void pushService.refreshIfEnabled({ userId: String(user.id) });
    // We intentionally don't depend on `permission` flipping — refresh runs
    // once per mount when already-granted to avoid token thrash.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, autoRefresh, support]);

  // ---- foreground / background-click bridge --------------------------------
  useEffect(() => {
    if (!onMessage) return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<IncomingPushMessage>).detail;
      if (detail) onMessage(detail);
    };
    window.addEventListener(PUSH_MESSAGE_EVENT, handler);
    return () => window.removeEventListener(PUSH_MESSAGE_EVENT, handler);
  }, [onMessage]);

  // ---- logout cleanup ------------------------------------------------------
  useEffect(() => {
    if (isAuthenticated) return;
    // User logged out — drop the FCM token so the next user doesn't inherit it.
    if (token) void pushService.disable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // ---- imperative actions --------------------------------------------------
  const enable = useCallback(async (): Promise<EnableResult> => {
    if (!user?.id) return { status: 'error', reason: 'Authentication required' } as EnableResult;
    setIsEnabling(true);
    setEnableError(null);
    try {
      const result = await pushService.enable({ userId: String(user.id) });
      // Users commonly grant permission from the feed, after the post-login
      // updateinfo request has already completed with `no-token`. Register the
      // newly-created token for the current session immediately; otherwise the
      // backend has no destination and browser pushes never arrive.
      if (result.status === 'enabled' && result.token && authToken) {
        await updateDeviceInfo(user.id, authToken, {
          deviceToken: result.token,
          chatDeviceToken: result.token,
        });
      }
      if (result.status === 'error' || result.status === 'unsupported') {
        setEnableError(result.reason ?? 'Unable to enable notifications');
      }
      return result;
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'Unable to enable notifications';
      setEnableError(reason);
      return { status: 'error', reason };
    } finally {
      setIsEnabling(false);
    }
  }, [authToken, user?.id]);

  const disable = useCallback(async () => {
    await pushService.disable();
  }, []);

  const dismiss = useCallback(() => {
    pushService.markOptedOut();
  }, []);

  return {
    support,
    permission,
    optedOut,
    token,
    enable,
    disable,
    dismiss,
    isEnabling,
    enableError,
  };
}
